'use client'
import React, { useEffect, useState, ReactNode } from "react"
import { useSelector } from "react-redux"
import { selectLanguage } from "@/app/redux/store"
import { usePathname, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import { getProjectById } from "@/app/adapters/api"

type TBreadCrumbProps = {
    homeElement: ReactNode,
    separator: ReactNode,
    containerClasses?: string,
    listClasses?: string,
    activeClasses?: string,
    capitalizeLinks?: boolean
}
const BreadcrumbsGeneral = ({ homeElement, separator, containerClasses, capitalizeLinks }: TBreadCrumbProps) => {
    const paths = usePathname()!
    const pathNames = paths.split('/').filter(path => path)
    const lang = useSelector(selectLanguage)
    const searchParams = useSearchParams()!
    const id = searchParams.get('id') || ''
    const [project, setProject]: any = useState('')

    const pathDisplayNames: { [key: string]: string } = {
        'quienes-somos': lang === 'es' ? 'Quiénes Somos' : 'About Us',
        'nuestro-impacto': lang === 'es' ? 'Nuestro Impacto' : 'Our Impact',
        'productos-inversion': lang === 'es' ? 'Cómo Invertir' : 'How to Invest',
        'startup-equity': lang === 'es' ? 'Inversiones en Capital' : 'Equity Investments',
        'empresas-lending': lang === 'es' ? 'Préstamos Sostenibles' : 'Sustainable Loans',
        'oportunidades-inversion': lang === 'es' ? 'Oportunidades de Inversión' : 'Investment Opportunities',
        'ejemplos-de-inversion': lang === 'es' ? 'Nuestro Portfolio' : 'Our Portfolio',
        'privacidad': lang === 'es' ? 'Política de Privacidad' : 'Privacy Policy',
        'informacion-basica': lang === 'es' ? 'Información Básica' : 'Basic Information',
        'condiciones-contratacion': lang === 'es' ? 'Condiciones de Contratación' : 'Terms and Conditions',
        'preguntas-frecuentes': lang === 'es' ? 'Preguntas Frecuentes' : 'FAQs',
        'mi-cuenta': lang === 'es' ? 'Mi cuenta' : 'My account',
    };

    useEffect(() => {
        if (id && pathNames.includes('investment') && !pathNames.includes('step2') && !pathNames.includes('step3') && !pathNames.includes('step4') && !pathNames.includes('step5')) {
            getProjectById(id)
                .then((response) => {
                    setProject(response?.data?.project?.name);

                    let itemListElement = pathNames.map((path, index) => {
                        const href = `/${pathNames.slice(0, index + 1).join('/')}`;
                        return {
                            "@type": "ListItem",
                            "position": index + 1,
                            "name": capitalizeLinks ? path.charAt(0).toUpperCase() + path.slice(1) : path,
                            "item": `https://www.bolsasocial.com${href}`,
                        };
                    });

                    if (response?.data?.project?.name) {
                        itemListElement.push({
                            "@type": "ListItem",
                            "position": itemListElement.length + 1,
                            "name": response.data.project.name,
                            "item": `https://www.bolsasocial.com/${pathNames.join('/')}/?id=${id}`,
                        });
                    }

                    const breadcrumbList = {
                        "@context": "https://schema.org",
                        "@type": "BreadcrumbList",
                        "itemListElement": itemListElement
                    };

                    const script = document.createElement('script');
                    script.type = "application/ld+json";
                    script.text = JSON.stringify(breadcrumbList);
                    document.head.appendChild(script);

                    return () => {
                        document.head.removeChild(script);
                    };
                });
        } else {
            const breadcrumbList = {
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": pathNames.map((path, index) => {
                    const href = `/${pathNames.slice(0, index + 1).join('/')}`;
                    return {
                        "@type": "ListItem",
                        "position": index + 1,
                        "name": capitalizeLinks ? path.charAt(0).toUpperCase() + path.slice(1) : path,
                        "item": `https://www.bolsasocial.com${href}`,
                    };
                })
            };

            const script = document.createElement('script');
            script.type = "application/ld+json";
            script.text = JSON.stringify(breadcrumbList);
            document.head.appendChild(script);

            return () => {
                document.head.removeChild(script);
            };
        }
    }, []);

    return (
        <div className="px-5">

            {
                paths === '/' || paths === '/entrar/' || paths === '/registro/' || paths.includes('step') ?
                    null
                    :
                    <ul className={containerClasses}>  
                        <li className="hover:underline mx-2 text-regular text-[#E57200]"><Link href={'/'}>{homeElement}</Link></li>
                        {pathNames.length > 0 && separator}
                        {pathNames.map((link, index) => {
                            let displayName = '';
                            let href = '';
                            if (link === 'investment' && project) {
                                displayName = lang === 'es' ? 'Oportunidades de Inversión' : 'Investment Opportunities';
                                href = lang === 'es' ? '/oportunidades-inversion' : '/investment';
                            } else {
                                displayName = pathDisplayNames[link] || (capitalizeLinks ? link[0].toUpperCase() + link.slice(1, link.length) : link);
                                href = `/${pathNames.slice(0, index + 1).join('/')}`;
                            }

                            const isLastElement = index === pathNames.length - 1;

                            return (
                                <React.Fragment key={index}>
                                    <li className={isLastElement && !project ? "font-bold mx-2 text-regular text-[#E57200]" : "hover:underline mx-2 text-regular text-[#E57200]"}>
                                        {isLastElement && !project ? (
                                            displayName.replace(/-/g, ' ')
                                        ) : (
                                            <Link href={href}>{displayName.replace(/-/g, ' ')}</Link>
                                        )}
                                    </li>
                                    {pathNames.length !== index + 1 && separator}
                                </React.Fragment>
                            );
                        })}
                        {project && (
                            <React.Fragment>
                                {separator}
                                <li className={"font-bold mx-2 text-regular text-[#E57200]"}>
                                    {project}
                                </li>
                            </React.Fragment>
                        )}
                    </ul>
            }
        </div>
    );
}

export default BreadcrumbsGeneral